import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { ConfirmationProgressStatus, setSplitConfirmationProgressByExam } from '../confirmation-progress.js';
import { ConfirmationMessage, Conversation } from '../conversation.js';
import { Exam } from '../exam.js';
import type { EvaluatedStudentBlock } from './agents/calculate-split-confidence.js';
import type { SplitIssueDetail } from './agents/split-file-agent.js';

function buildBlockUidOrderMap(
  issueDetails: SplitIssueDetail[],
  studentBlocks?: EvaluatedStudentBlock[],
): Map<string, number> {
  const blockUidOrderMap = new Map<string, number>();

  if (Array.isArray(studentBlocks) && studentBlocks.length > 0) {
    for (let index = 0; index < studentBlocks.length; index++) {
      const block = studentBlocks[index];
      const blockId = String(block?.id ?? block?.block_uid ?? '').trim();
      if (blockId) {
        blockUidOrderMap.set(blockId, index + 1);
      }
    }
  }

  if (blockUidOrderMap.size === 0) {
    let nextOrder = 1;
    for (const detail of issueDetails) {
      if (typeof detail.blockUid !== 'string' || detail.blockUid.trim().length === 0) continue;
      if (blockUidOrderMap.has(detail.blockUid)) continue;
      blockUidOrderMap.set(detail.blockUid, nextOrder);
      nextOrder += 1;
    }
  }

  return blockUidOrderMap;
}

function getStudentLabel(detail: SplitIssueDetail, blockUidOrderMap: Map<string, number>): string {
  if (typeof detail.studentName === 'string' && detail.studentName.trim().length > 0) {
    return detail.studentName.trim();
  }
  if (
    typeof detail.blockUid === 'string' &&
    detail.blockUid.trim().length > 0 &&
    blockUidOrderMap.has(detail.blockUid)
  ) {
    return `__STUDENT__${blockUidOrderMap.get(detail.blockUid)}`;
  }
  if (Number.isFinite(detail.studentId)) {
    return `__STUDENT__${detail.studentId}`;
  }
  return '__UNKNOWN__';
}

function uniqueStudentLabels(details: SplitIssueDetail[], blockUidOrderMap: Map<string, number>): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];

  for (const detail of details) {
    const key =
      typeof detail.blockUid === 'string' && detail.blockUid.trim().length > 0
        ? `uid:${detail.blockUid}`
        : Number.isFinite(detail.studentId)
          ? `id:${detail.studentId}`
          : `name:${String(detail.studentName || '')}`;

    if (seen.has(key)) continue;
    seen.add(key);
    labels.push(getStudentLabel(detail, blockUidOrderMap));
  }

  return labels;
}

export function formatSplitIssueDetails(
  issueDetails: SplitIssueDetail[],
  totalStudents?: number,
  studentsWithoutName?: number,
  studentBlocks?: EvaluatedStudentBlock[],
): string {
  if (!Array.isArray(issueDetails) || issueDetails.length === 0) return '';

  const blockUidOrderMap = buildBlockUidOrderMap(issueDetails, studentBlocks);
  const issues: Array<{ key: string; args?: any; type: 'error' | 'warning' }> = [];

  const hasPageAssignmentIssue = issueDetails.some(detail => detail.code === 'pages_not_assigned_once');
  if (hasPageAssignmentIssue) {
    issues.push({ key: 'splitIssues.pageAssignmentError', type: 'error' });
  }

  const nonConsecutiveNames = uniqueStudentLabels(
    issueDetails.filter(detail => detail.code === 'non_consecutive_pages'),
    blockUidOrderMap,
  );
  if (nonConsecutiveNames.length > 0) {
    issues.push({ key: 'splitIssues.nonConsecutive', args: { names: nonConsecutiveNames.join(', ') }, type: 'error' });
  }

  const uncertainNames = uniqueStudentLabels(
    issueDetails.filter(detail => detail.code === 'missing_start_boundary' || detail.code === 'transition_uncertain'),
    blockUidOrderMap,
  );
  if (uncertainNames.length > 0) {
    issues.push({ key: 'splitIssues.uncertainty', args: { names: uncertainNames.join(', ') }, type: 'error' });
  }

  const outlierLongExtremeNames = uniqueStudentLabels(
    issueDetails.filter(detail => detail.code === 'outlier_long_extreme'),
    blockUidOrderMap,
  );
  if (outlierLongExtremeNames.length > 0) {
    issues.push({
      key:
        outlierLongExtremeNames.length === 1
          ? 'splitIssues.outlierLongExtremeSingle'
          : 'splitIssues.outlierLongExtremePlural',
      args: { names: outlierLongExtremeNames.join(', ') },
      type: 'error',
    });
  }

  const outlierShortExtremeNames = uniqueStudentLabels(
    issueDetails.filter(detail => detail.code === 'outlier_short_extreme'),
    blockUidOrderMap,
  );
  if (outlierShortExtremeNames.length > 0) {
    issues.push({
      key:
        outlierShortExtremeNames.length === 1
          ? 'splitIssues.outlierShortExtremeSingle'
          : 'splitIssues.outlierShortExtremePlural',
      args: { names: outlierShortExtremeNames.join(', ') },
      type: 'error',
    });
  }

  const outlierLongNames = uniqueStudentLabels(
    issueDetails.filter(detail => detail.code === 'outlier_long' || detail.code === 'length_outlier'),
    blockUidOrderMap,
  );
  if (outlierLongNames.length > 0) {
    issues.push({
      key: outlierLongNames.length === 1 ? 'splitIssues.outlierLongSingle' : 'splitIssues.outlierLongPlural',
      args: { names: outlierLongNames.join(', ') },
      type: 'warning',
    });
  }

  const outlierShortNames = uniqueStudentLabels(
    issueDetails.filter(detail => detail.code === 'outlier_short'),
    blockUidOrderMap,
  );
  if (outlierShortNames.length > 0) {
    issues.push({
      key: outlierShortNames.length === 1 ? 'splitIssues.outlierShortSingle' : 'splitIssues.outlierShortPlural',
      args: { names: outlierShortNames.join(', ') },
      type: 'warning',
    });
  }

  const normalizedTotalStudents = Number.isFinite(Number(totalStudents))
    ? Math.max(0, Math.round(Number(totalStudents)))
    : 0;
  const normalizedStudentsWithoutName = Number.isFinite(Number(studentsWithoutName))
    ? Math.max(0, Math.round(Number(studentsWithoutName)))
    : 0;
  const hasMissingStudentNameIssue = issueDetails.some(detail => detail.code === 'missing_student_name');
  if (hasMissingStudentNameIssue && normalizedTotalStudents > 0 && normalizedStudentsWithoutName > 0) {
    const key =
      normalizedTotalStudents === 1 ? 'splitIssues.missingStudentNameSingle' : 'splitIssues.missingStudentNamePlural';
    issues.push({
      key,
      args: { missingCount: normalizedStudentsWithoutName, totalCount: normalizedTotalStudents },
      type: 'warning',
    });
  }

  const nameUncertainNames = uniqueStudentLabels(
    issueDetails.filter(detail => detail.code === 'name_uncertain'),
    blockUidOrderMap,
  );
  if (nameUncertainNames.length > 0) {
    issues.push({ key: 'splitIssues.uncertainty', args: { names: nameUncertainNames.join(', ') }, type: 'warning' });
  }

  const splitFailures = issueDetails.filter(detail => detail.code === 'split_agent_failure');
  if (splitFailures.length > 0) {
    const splitSources = Array.from(
      new Set(
        splitFailures.map(detail => detail.sourceFileName).filter((name): name is string => !!name && name.length > 0),
      ),
    );
    const suffix = splitSources.length > 0 ? ` (${splitSources.join(', ')})` : '';
    issues.push({ key: 'splitIssues.splitAgentFailure', args: { suffix }, type: 'error' });
  }

  return JSON.stringify(issues);
}

export const createSubmissionSplitConfirmationMutation = registerMutation({
  operationName: 'createSubmissionSplitConfirmationMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'conversationId', type: () => String },
    { name: 'studentsFound', type: () => Int },
    { name: 'totalStudents', type: () => Int },
    { name: 'studentsWithoutName', type: () => Int },
    { name: 'minPagesPerStudent', type: () => Int },
    { name: 'maxPagesPerStudent', type: () => Int },
    { name: 'problem', type: () => String },
    { name: 'hasErrors', type: () => Boolean },
    { name: 'hasWarnings', type: () => Boolean },
  ],
  resolve:
    (
      _examId: string,
      conversationId: string,
      studentsFound: number,
      totalStudents: number,
      studentsWithoutName: number,
      minPagesPerStudent: number,
      maxPagesPerStudent: number,
      problem: string,
      hasErrors: boolean,
      hasWarnings: boolean,
    ) =>
    async () => {
      const conversationCollection = await getCollection(Conversation);
      const examCollection = await getCollection(Exam);
      const conversation = await conversationCollection.findOne({ _id: conversationId });

      const markUiRefresh = async () => {
        if (!_examId) return;
        await examCollection.updateOne({ _id: _examId }, { $set: { 'examState.uiNeedsRefresh': true } });
      };

      if (!conversation) return false;

      const messages = Array.isArray((conversation as any).messages)
        ? ([...(conversation as any).messages] as any[])
        : [];

      const normalizedProblem = String(problem || '').trim();
      const hasProblem = normalizedProblem.length > 0;
      const normalizedCount = Number.isFinite(studentsFound) ? Math.max(0, Math.round(studentsFound)) : 0;
      const normalizedMinPages = Number.isFinite(minPagesPerStudent) ? Math.max(0, Math.round(minPagesPerStudent)) : 0;
      const normalizedMaxPages = Number.isFinite(maxPagesPerStudent) ? Math.max(0, Math.round(maxPagesPerStudent)) : 0;
      const pageRangeText = `${normalizedMinPages}-${normalizedMaxPages}`;
      const issueMode: 'error' | 'warning' | 'none' = hasErrors ? 'error' : hasWarnings ? 'warning' : 'none';

      // PAYLOAD FÜR DAS FRONTEND BAUEN
      const payload = {
        count: normalizedCount,
        range: pageRangeText,
        issueMode,
        problemPayload: normalizedProblem,
      };

      const content = `[[SPLIT_CONFIRMATION]]${JSON.stringify(payload)}`;

      const newConfirmationMessage = new ConfirmationMessage({
        content,
        confirmationType: 'submissionSplit',
        confirmed: false,
        confirmedBy: 'pending',
      }).toObject();

      messages.push(newConfirmationMessage);
      const targetIndex = messages.length - 1;
      const targetMessageId = String(messages[targetIndex]?._id ?? messages[targetIndex]?.id ?? '').trim();

      // total/finished are passed explicitly rather than relying on setSplitConfirmationProgressByExam's
      // read-then-merge of the existing state, since that state was written moments ago by the split loop in
      // runSubmissionSplit — the same reasoning the self-upload auto-confirm branch there already documents.
      // Without this, the status bar's ring (StepsIconSidebar) can render at less than 100% while awaiting
      // confirmation instead of staying fully filled the way the structure step's does.
      await setSplitConfirmationProgressByExam(_examId, {
        status: ConfirmationProgressStatus.STARTED,
        confirmed: false,
        messageId: targetMessageId,
        message: 'progress.confirmationRequested',
        total: normalizedCount,
        finished: normalizedCount,
      });

      await conversationCollection.updateOne(
        { _id: conversationId },
        {
          $set: {
            messages: messages as any,
            lastUpdated: new Date(),
            ...(hasProblem && hasErrors ? { pendingCanvasOpen: 'submissioncheck' } : {}),
          },
        },
      );
      await markUiRefresh();
      return true;
    },
});
